// src/services/bungieIconService.ts
import { API_CONFIG } from '../components/config/api';

// Cache for already loaded icons to reduce requests
interface IconCache {
  [key: string]: string;
}

// In-memory cache to avoid repeated requests for the same icon
const iconCache: IconCache = {};

// Keep track of failed icon URLs to avoid repeated failed requests
const failedIconUrls = new Set<string>();

// Base64 encoded SVG fallback icons
export const FALLBACK_ICONS = {
  DEFAULT: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+CiAgPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIgLz4KICA8cGF0aCBkPSJNMTIgNkw3IDEwLjVMOC41IDEyTDExIDkuNVYxOEgxM1Y5LjVMMTUuNSAxMkwxNyAxMC41TDEyIDZaIiBmaWxsPSIjRjJDMzUwIiAvPgo8L3N2Zz4=',
  
  RAID: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+CiAgPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIgLz4KICA8cGF0aCBkPSJNMTIgMkwxNC41IDlIMjBMMTUuNSAxMy41TDE3IDIwTDEyIDE2TDcgMjBMOC41IDEzLjVMNCA5SDkuNUwxMiAyWiIgZmlsbD0iI0YyQzM1MCIgLz4KPC9zdmc+',
  
  CRUCIBLE: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+CiAgPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIgLz4KICA8cGF0aCBkPSJNMTIgM0w0IDEwLjVMNyAxNEwxMCAxMS41VjIxSDE0VjExLjVMMTcgMTRMMjAgMTAuNUwxMiAzWiIgZmlsbD0iI0QxNTU0RSIgLz4KPC9zdmc+',
  
  VANGUARD: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+CiAgPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIgLz4KICA8cGF0aCBkPSJNMTIgM0w3IDhWMTJMMTIgMTdMMTcgMTJWOEwxMiAzWk05IDEwTDEyIDEzTDE1IDEwVjlMMTIgNkw5IDlWMTBaIiBmaWxsPSIjM0U5NUQxIiAvPgo8L3N2Zz4=',
  
  TRIALS: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+CiAgPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIgLz4KICA8cGF0aCBkPSJNMTIgMkwxNCA1TDE3IDZMMTkgOUwxNyAxMkwxOSAxNUwxNyAxOEwxNCAyMEwxMiAyMkwxMCAxOUw3IDE4TDUgMTVMNyAxMkw1IDlMNyA2TDEwIDRMMTIgMloiIGZpbGw9IiNGMkMzNTAiIC8+Cjwvc3ZnPg==',
  
  DESTINATION: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+CiAgPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIgLz4KICA8cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJDMiAxNy41MiA2LjQ4IDIyIDEyIDIyQzE3LjUyIDIyIDIyIDE3LjUyIDIyIDEyQzIyIDYuNDggMTcuNTIgMiAxMiAyWk00IDEyQzQgMTEuMzkgNC4wOCAxMC43OSA0LjIxIDEwLjIyTDggMTRWMTVDOCAxNi4xIDguOSAxNyAxMCAxN1YxOS45M0M2LjU0IDE5LjQ0IDQgMTYuMDQgNCAxMlpNMTcuOSAxNy4zOUMxNy42NCAxNi41OCAxNi45IDE2IDE2IDE2SDE1VjEzQzE1IDEyLjQ1IDE0LjU1IDEyIDE0IDEySDhWMTBIMTBDMTAuNTUgMTAgMTEgOS41NSAxMSA5VjdIMTNDMTQuMSA3IDE1IDYuMSAxNSA1VjQuNTlDMTcuOTMgNS43OCAyMCA4LjY1IDIwIDEyQzIwIDE0LjA4IDE5LjIxIDE1Ljk3IDE3LjkgMTcuMzlaIiBmaWxsPSIjM0U5NUQxIiAvPgo8L3N2Zz4=',
  
  GAMBIT: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+CiAgPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIgLz4KICA8cGF0aCBkPSJNNCA0VjIwSDIwVjRINFpNMTggMThINlY2SDE4VjE4Wk0xMSA4VjE2TDE1IDEyTDExIDhaIiBmaWxsPSIjNTNBMzZBIiAvPgo8L3N2Zz4=',
  
  TRIUMPHS: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+CiAgPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIgLz4KICA8cGF0aCBkPSJNMTUgM0g5VjcuNUw0IDEwTDkgMTIuNVYxN0gxNVYxMi41TDIwIDEwTDE1IDcuNVYzWiIgZmlsbD0iI0YyQzM1MCIgLz4KPC9zdmc+',
  
  LOSTSECTOR: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+CiAgPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIgLz4KICA8cGF0aCBkPSJNMjEgMTFIMTV2LTFoLTFWOWgtMVY4aC0xVjdIOVY2SDhWOUg3djNINlY5SDNWOGgydi0ySDNWNWgydi0yaDNWMmgzdjFoNXYyaC0zdjFoM3YxaDF2MWgxdjJoLTF2MWgtMXYxaC01djFINnYxaDN2MkgzdjJoMnYySDN2MWgydjJoOHYtMWgydi0yaDF2MWgxdjJoMXYxaDF2MWgydi0yaC0xdi0xaC0xdi0yaDN2LTJoMXpNMTEgN1Y1aDJ2MmgtMnoiIGZpbGw9IiM1M0EzNkEiLz4KPC9zdmc+'
};

// Debug flag - set to true to enable console logging for debugging
const DEBUG_MODE = true;

// Get a fallback icon based on URL or path
const getFallbackIcon = (path: string): string => {
  const pathLower = path.toLowerCase();
  
  if (pathLower.includes('raid') || pathLower.includes('dungeon')) {
    return FALLBACK_ICONS.RAID;
  } else if (pathLower.includes('crucible') || pathLower.includes('pvp')) {
    return FALLBACK_ICONS.CRUCIBLE;
  } else if (pathLower.includes('trials')) {
    return FALLBACK_ICONS.TRIALS;
  } else if (pathLower.includes('vanguard') || pathLower.includes('strike')) {
    return FALLBACK_ICONS.VANGUARD;
  } else if (pathLower.includes('destination') || pathLower.includes('location')) {
    return FALLBACK_ICONS.DESTINATION;
  } else if (pathLower.includes('gambit')) {
    return FALLBACK_ICONS.GAMBIT;
  } else if (pathLower.includes('triumph') || pathLower.includes('title')) {
    return FALLBACK_ICONS.TRIUMPHS;
  } else if (pathLower.includes('lost sector') || pathLower.includes('loststector')) {
    return FALLBACK_ICONS.LOSTSECTOR;
  }
  
  return FALLBACK_ICONS.DEFAULT;
};

// Helper to create a colored placeholder with text
export const createTextIconPlaceholder = (text: string, bg = '#374151', fg = '#F2C350'): string => {
  // Take first letter or first two letters if name has multiple words
  const chars = text.trim().split(/\s+/).map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase();
  
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <rect width="24" height="24" fill="${bg}" rx="4" />
      <text x="12" y="16" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="${fg}" text-anchor="middle">${chars}</text>
    </svg>
  `;
  
  return `data:image/svg+xml;base64,${btoa(svg.trim())}`;
};

/**
 * Direct access to Bungie URLs - use as a backup if proxy fails
 * @param path The Bungie icon path 
 * @returns A direct URL to the Bungie.net resource
 */
export const getDirectBungieUrl = (path: string): string => {
  if (!path) return FALLBACK_ICONS.DEFAULT;
  
  // If it's already a full URL, return it
  if (path.startsWith('http')) return path;
  
  // Clean the path and ensure it starts with a slash
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // Return the full URL
  return `https://www.bungie.net${cleanPath}`;
};

/**
 * Try to fetch and store a Bungie icon from our proxy
 * Useful for prefetching or testing if an icon can be loaded
 * @param iconPath Path to the icon
 * @returns Promise that resolves to true if the icon was successfully fetched
 */
export const tryFetchBungieIcon = async (iconPath: string): Promise<boolean> => {
  if (!iconPath) return false;
  
  try {
    // Get the proxy URL
    const proxyUrl = getBungieIconUrl(iconPath, false);
    
    // Try to fetch the icon
    const response = await fetch(proxyUrl);
    if (response.ok) {
      // If successful, mark this URL as not failed
      failedIconUrls.delete(iconPath);
      return true;
    }
    
    // If we get here, the fetch failed
    if (DEBUG_MODE) console.warn(`Failed to fetch icon: ${iconPath}`);
    failedIconUrls.add(iconPath);
    return false;
  } catch (error) {
    if (DEBUG_MODE) console.error(`Error fetching icon: ${iconPath}`, error);
    failedIconUrls.add(iconPath);
    return false;
  }
};

/**
 * Resolves a Bungie icon path to the correct URL, using our API proxy for caching
 * Includes fallback handling for when the proxy endpoint fails
 * 
 * @param iconPath The path to the icon from Bungie API (usually starts with /)
 * @param useFallbackForFailed Whether to return fallback icons for previously failed URLs
 * @returns The URL to the icon, proxied through our API for caching, or a fallback SVG
 */
export const getBungieIconUrl = (
  iconPath: string | null | undefined, 
  useFallbackForFailed = true
): string => {
  if (!iconPath) {
    if (DEBUG_MODE) console.log('No icon path provided, using default fallback');
    return FALLBACK_ICONS.DEFAULT;
  }
  
  // For built-in fallback icon keys like '/triumphs', '/raid', etc.
  if (iconPath.startsWith('/') && iconPath.length < 12) {
    const key = iconPath.substring(1).toUpperCase();
    if (FALLBACK_ICONS[key as keyof typeof FALLBACK_ICONS]) {
      if (DEBUG_MODE) console.log(`Using built-in fallback for ${iconPath}`);
      return FALLBACK_ICONS[key as keyof typeof FALLBACK_ICONS];
    }
  }
  
  // Special case for lost sector (lower case because it might be mixed case)
  if (iconPath.toLowerCase().includes('lostsector') || iconPath.toLowerCase().includes('lost sector')) {
    return FALLBACK_ICONS.LOSTSECTOR;
  }
  
  // If the icon is already cached as failed and we're using fallbacks, return the fallback immediately
  if (useFallbackForFailed && failedIconUrls.has(iconPath)) {
    if (DEBUG_MODE) console.log(`Using fallback for previously failed icon: ${iconPath}`);
    return getFallbackIcon(iconPath);
  }
  
  // If it's already in the in-memory cache, return it
  if (iconCache[iconPath]) {
    return iconCache[iconPath];
  }
  
  // Check if the path is already a data URL (like our fallbacks)
  if (iconPath.startsWith('data:')) {
    return iconPath;
  }
  
  // Make sure we have a clean path to work with
  let bungieIconPath = iconPath;
  
  // If it's a full Bungie URL, extract just the path
  if (bungieIconPath.startsWith('https://www.bungie.net')) {
    bungieIconPath = bungieIconPath.replace('https://www.bungie.net', '');
  }
  
  // Make sure the path starts with a slash
  if (!bungieIconPath.startsWith('/')) {
    bungieIconPath = '/' + bungieIconPath;
  }

  // Determine which API endpoint to use for the proxy
  // This is a direct fix for the issue - we'll try a direct URL first
  // But still cache the proxy URL for future use
  const useDirectUrl = true; // Change this to false to use the proxy
  
  if (useDirectUrl) {
    // Just return the direct Bungie URL
    const directUrl = getDirectBungieUrl(bungieIconPath);
    iconCache[iconPath] = directUrl;
    return directUrl;
  } else {
    // Use the proxy as before
    const proxyDomain = window.location.origin;
    const proxyUrl = `${proxyDomain}/proxy-bungie-icon?url=${encodeURIComponent('https://www.bungie.net' + bungieIconPath)}`;
    iconCache[iconPath] = proxyUrl;
    return proxyUrl;
  }
};

/**
 * Handles image load errors by providing fallback icons
 * Use this in onError handlers for <img> elements
 */
export const handleIconError = (
  event: React.SyntheticEvent<HTMLImageElement, Event>,
  iconPath: string | null = null,
  defaultText: string = ''
): void => {
  const target = event.currentTarget;
  target.onerror = null; // Prevent infinite error loops
  
  if (DEBUG_MODE) console.warn(`Icon load error for: ${iconPath || target.src}`);
  
  // Mark the URL as failed for future reference
  if (iconPath) {
    failedIconUrls.add(iconPath);
  }
  
  // Get a fallback icon based on the path or use text-based placeholder
  const fallbackSrc = iconPath 
    ? getFallbackIcon(iconPath)
    : (defaultText 
        ? createTextIconPlaceholder(defaultText) 
        : FALLBACK_ICONS.DEFAULT);
  
  // Apply the fallback
  target.src = fallbackSrc;
};

/**
 * Preloads a set of icons to improve UI responsiveness
 */
export const preloadBungieIcons = async (iconPaths: (string | null | undefined)[]): Promise<void> => {
  const validPaths = iconPaths.filter(path => path) as string[];
  
  // Process in batches to avoid overwhelming the network
  const batchSize = 5;
  for (let i = 0; i < validPaths.length; i += batchSize) {
    const batch = validPaths.slice(i, i + batchSize);
    // Create image objects and set the src to preload
    await Promise.all(batch.map(path => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => {
          // Mark as failed and resolve anyway to continue
          if (path) failedIconUrls.add(path);
          resolve();
        };
        img.src = getBungieIconUrl(path);
      });
    }));
  }
};

/**
 * Clears the icon cache
 */
export const clearIconCache = (): void => {
  Object.keys(iconCache).forEach(key => {
    delete iconCache[key];
  });
  failedIconUrls.clear();
};